/**
 * Registry of organisation door / window types (definitions whose spec has
 * `opening`). They are NOT sketch-object types: a placed door/window is an
 * OPENING_SKETCH whose `door type` / `window type` holds the type id
 * (`c_<definitionId>`), and its glyph comes from openingGlyphs.ts. Loaded
 * with the rest of the library (objectLibraryRegistration.ts).
 *
 * Each type's params are reactive OPENING_SKETCH properties
 * (`openingsketch_<typeId>_<key>`), shown while the opening uses the type.
 */

import { type CustomOpeningTypeId, type ObjectDefinitionSpec, type ObjectOpeningKind } from 'shared-types';
import { OPENING_PARAM_SCOPE, registerParamProperties, unregisterParamProperties } from '../../paramSchema';
import { getSketchObjectLibraryConfig, isSketchObjectTypeHidden } from '../../registry';
import { specToParamSchema } from '../customSketchObjectType';

export interface CustomOpeningType {
  readonly id: CustomOpeningTypeId;
  readonly definitionId: string;
  readonly label: string;
  readonly kind: ObjectOpeningKind;
  readonly spec: ObjectDefinitionSpec;
  /** False for archived definitions: placed openings keep drawing it, the dropdowns leave it out. */
  readonly listed: boolean;
}

const types = new Map<string, CustomOpeningType>();
const listeners = new Set<() => void>();
let version = 0;

function notify(): void {
  version++;
  for (const listener of listeners) listener();
}

/** Visibility of a type's params: the opening's door / window type must be this type. */
function paramVisibility(type: CustomOpeningType) {
  return [
    { propertyIdentifier: 'openingsketch_kind', value: type.kind },
    { propertyIdentifier: type.kind === 'DOOR' ? 'openingsketch_door_type' : 'openingsketch_window_type', value: type.id },
  ];
}

function register(type: CustomOpeningType): void {
  types.set(type.id, type);
  registerParamProperties(type.id, specToParamSchema(type.spec), OPENING_PARAM_SCOPE, paramVisibility(type));
}

function unregister(type: CustomOpeningType): void {
  unregisterParamProperties(type.id, specToParamSchema(type.spec), OPENING_PARAM_SCOPE);
  types.delete(type.id);
}

/** Replace the whole set (one library load), with ONE change notification. */
export function setCustomOpeningTypes(next: readonly CustomOpeningType[]): void {
  const wanted = new Map(next.map((t) => [t.id, t]));
  let changed = false;
  for (const existing of [...types.values()]) {
    const replacement = wanted.get(existing.id);
    if (replacement && JSON.stringify(replacement) === JSON.stringify(existing)) {
      wanted.delete(existing.id);
      continue;
    }
    unregister(existing);
    changed = true;
  }
  for (const type of wanted.values()) {
    register(type);
    changed = true;
  }
  if (changed) notify();
}

export function getCustomOpeningType(id: string): CustomOpeningType | null {
  return types.get(id) ?? null;
}

export function getCustomOpeningTypes(kind?: ObjectOpeningKind): CustomOpeningType[] {
  const all = [...types.values()];
  return kind ? all.filter((t) => t.kind === kind) : all;
}

/** The types offered for `kind` in dropdowns and the library picker: listed, not hidden by the organisation, in its order. */
export function offeredCustomOpeningTypes(kind: ObjectOpeningKind): CustomOpeningType[] {
  const order = new Map(getSketchObjectLibraryConfig().typeOrder.map((p) => [p.typeId, p.order]));
  return getCustomOpeningTypes(kind)
    .filter((t) => t.listed && !isSketchObjectTypeHidden(t.id))
    .map((type, index) => ({ type, index }))
    .sort((a, b) => (order.get(a.type.id) ?? Infinity) - (order.get(b.type.id) ?? Infinity) || a.index - b.index)
    .map(({ type }) => type);
}

/** Subscribe to door / window type changes (library loads). Returns the unsubscribe. */
export function onCustomOpeningTypesChanged(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Bumped on every change (useSyncExternalStore snapshot). */
export function customOpeningTypesVersion(): number {
  return version;
}
